/**
 * Gift Product — gift-product.js
 * Nexvo (FoxTheme) compatible — vanilla JS, no jQuery dependency.
 *
 * Supports multiple section instances on the same page via
 * window.GiftProductSections[sectionId] config objects.
 */

(function () {
  'use strict';

  // ─── Currency formatter ───────────────────────────────────────────────────
  const formatter = new Intl.NumberFormat('en-PK', {
    style: 'currency',
    currency: 'PKR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  // ─── Helpers ──────────────────────────────────────────────────────────────

  /** Wait for a DOM element matching `selector`, up to `timeout` ms. */
  function waitForElement(selector, timeout = 5000) {
    return new Promise((resolve, reject) => {
      const el = document.querySelector(selector);
      if (el) return resolve(el);
      const observer = new MutationObserver(() => {
        const found = document.querySelector(selector);
        if (found) {
          observer.disconnect();
          resolve(found);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => {
        observer.disconnect();
        reject(new Error(`Element "${selector}" not found within ${timeout}ms`));
      }, timeout);
    });
  }

  /** Show element (remove display:none). */
  function show(el) {
    if (!el) return;
    el.style.display = '';
    el.removeAttribute('aria-hidden');
  }

  /** Hide element (set display:none). */
  function hide(el) {
    if (!el) return;
    el.style.display = 'none';
    el.setAttribute('aria-hidden', 'true');
  }

  /** Toggle visibility with optional force. */
  function setVisible(el, visible) {
    visible ? show(el) : hide(el);
  }

  /** POST to Shopify cart/add.js via fetch. */
  function cartAdd(formData) {
    const cartAddUrl =
      (window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_add_url) ||
      '/cart/add.js';

    return fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(formData),
    });
  }

  /** Briefly flash an element with a CSS class. */
  function flashError(el, duration = 4000) {
    if (!el) return;
    el.classList.add('cserror');
    setTimeout(() => el.classList.remove('cserror'), duration);
  }

  // ─── Date helpers ─────────────────────────────────────────────────────────

  /** Return the minimum allowed delivery date (today + 3 working days). */
  function getMinDeliveryDate() {
    const today = new Date();
    let added = 0;
    const d = new Date(today);
    while (added < 3) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added++; // skip Sat (6) and Sun (0)
    }
    return d;
  }

  // ─── GiftProduct class ────────────────────────────────────────────────────

  class GiftProduct {
    constructor(config) {
      this.sectionId = config.sectionId;
      this.cfg = config;

      // State
      this.selectedPackage = '';
      this.selectedSize = '';
      this.totalPrice = 0;

      // DOM roots
      this.root = document.getElementById(`GiftProduct-${this.sectionId}`);
      if (!this.root) return;

      this.errorOverlay = document.getElementById(config.errorOverlayId);
      this.giftActions = document.getElementById(config.giftActionsId);
      this.productsArea = document.getElementById(config.productAreaId);
      this.packageSelector = document.getElementById(config.packageSelectorId);
      this.sizeSelector = document.getElementById(config.sizeSelectorId);
      this.defaultSelector = document.getElementById(config.defaultSelectorId);
      this.totalPriceEl = this.root.querySelector('.pdp-totalprice');
      this.deliveryInput = document.getElementById(config.deliveryDateId);
      this.qtyInput = document.getElementById(config.csqtyId);
      this.notesWrap = this.root.querySelector('.gift-product__notes-wrap');
      this.notesTextarea = this.root.querySelector('.ds-notes');
      this.instructionsTextarea = this.root.querySelector('.ds-instructions');
      this.timeslotError = this.root.querySelector('.timeslot-error');

      this._initProductTypes();
      this._bindEvents();
      this._initDatePicker();
    }

    // ── Init ───────────────────────────────────────────────────────────────

    /** Set data-limit on each parent-products-type and hide empty ones. */
    _initProductTypes() {
      this.root.querySelectorAll('.parent-products-type').forEach((typeEl) => {
        const firstItem = typeEl.querySelector('.cs-product-item');
        if (!firstItem) {
          hide(typeEl);
          typeEl.classList.add('ds-none');
          return;
        }

        const limit = parseInt(firstItem.dataset.limit || '1', 10);
        typeEl.dataset.limit = limit;

        // Inject limit hint into header if not already there
        const headerH3 = typeEl.querySelector('.gift-product__type-title');
        if (headerH3 && !headerH3.querySelector('.gift-product__type-limit')) {
          const hint = document.createElement('span');
          hint.className = 'gift-product__type-limit';
          hint.textContent = `(Choose any ${limit})`;
          headerH3.appendChild(hint);
        }
      });
    }

    _bindEvents() {
      // Package radio
      this.root.querySelectorAll('.ds-package-radio').forEach((radio) => {
        radio.addEventListener('change', (e) => this._onPackageChange(e));
      });

      // Size radio
      this.root.querySelectorAll('.ds-size-radio').forEach((radio) => {
        radio.addEventListener('change', (e) => this._onSizeChange(e));
      });

      // Default/desired radio
      this.root.querySelectorAll('.ds-default-radio').forEach((radio) => {
        radio.addEventListener('change', (e) => this._onDefaultChange(e));
      });

      // Product item click + keyboard
      this.root.querySelectorAll('.cs-product-item').forEach((item) => {
        item.addEventListener('click', (e) => this._onProductClick(e.currentTarget));
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._onProductClick(e.currentTarget);
          }
        });
      });

      // Add to cart
      const addBtn = this.root.querySelector('.addtobtn');
      if (addBtn) {
        addBtn.addEventListener('click', () => this._onAddToCart());
        addBtn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._onAddToCart();
          }
        });
      }

      // Error close
      if (this.errorOverlay) {
        const closeBtn = this.errorOverlay.querySelector('.ds-error-close');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => hide(this.errorOverlay));
        }
        const backdrop = this.errorOverlay.querySelector('.gift-product__error-backdrop');
        if (backdrop) {
          backdrop.addEventListener('click', () => hide(this.errorOverlay));
        }
      }
    }

    _initDatePicker() {
      if (!this.deliveryInput) return;

      // Wait for daterangepicker to be available
      const waitForDRP = () => {
        if (typeof window.$ === 'function' && typeof window.$.fn.daterangepicker === 'function') {
          this._attachDateRangePicker();
        } else if (typeof window.daterangepicker !== 'undefined') {
          // Vanilla fallback — library loaded but no jQuery wrapper
          // Use basic input[type=date] as a graceful degradation
          this.deliveryInput.type = 'date';
          const minDate = getMinDeliveryDate();
          this.deliveryInput.min = minDate.toISOString().split('T')[0];
        } else {
          setTimeout(waitForDRP, 300);
        }
      };

      waitForDRP();
    }

    _attachDateRangePicker() {
      const $ = window.$;
      const input = this.deliveryInput;
      const minDate = getMinDeliveryDate();

      $(input).daterangepicker(
        {
          singleDatePicker: true,
          minDate: minDate,
          locale: { format: 'YYYY-MM-DD' },
          isInvalidDate: function (date) {
            const day = date._d.getDay();
            return day === 0 || day === 6; // block Sat & Sun
          },
        },
        function (chosen) {
          input.value = chosen.format('YYYY-MM-DD');
        }
      );
    }

    // ── Event handlers ─────────────────────────────────────────────────────

    _onPackageChange(e) {
      const pkg = e.target.value;
      if (!pkg || pkg === 'null') return;

      this.selectedPackage = pkg;

      // Reset product selections
      this._clearProductSelections();

      // Show size selector, hide size groups, show matching group
      show(this.sizeSelector);
      this.root.querySelectorAll('.size-options').forEach((g) => hide(g));
      const matchingGroup = this.root.querySelector(`.opt-${pkg}`);
      if (matchingGroup) show(matchingGroup);

      // If a size was already selected, check if it still exists for new package
      const checkedSizeRadio = this.root.querySelector('.ds-size-radio:checked');
      if (checkedSizeRadio) {
        // Uncheck if it doesn't belong to the new package group
        if (!matchingGroup || !matchingGroup.contains(checkedSizeRadio)) {
          checkedSizeRadio.checked = false;
          this.selectedSize = '';
          hide(this.defaultSelector);
          hide(this.productsArea);
          hide(this.giftActions);
        } else {
          // Re-trigger size logic
          this._showProductsForCombo(this.selectedPackage, this.selectedSize);
        }
      }

      // Show package image if size already selected
      if (this.selectedSize) {
        this._updatePackageImage();
        this._updateDescription();
      }
    }

    _onSizeChange(e) {
      const size = e.target.value;
      if (!size || size === 'null') return;

      this.selectedSize = size;

      // Reset product selections
      this._clearProductSelections();

      // Update basket price in total
      this._recalcTotal();

      // Show default/desired selector
      show(this.defaultSelector);

      // Update image + description
      this._updatePackageImage();
      this._updateDescription();

      // Show products area
      this._showProductsForCombo(this.selectedPackage, this.selectedSize);

      // Reset default radio
      this.root.querySelectorAll('.ds-default-radio').forEach((r) => (r.checked = false));
      hide(this.giftActions);
    }

    _onDefaultChange(e) {
      const val = e.target.value;
      const pkg = this.selectedPackage;
      const size = this.selectedSize;
      if (!pkg || !size) return;

      show(this.productsArea);
      show(this.giftActions);

      const wrapper = this.root.querySelector(`.ds-${pkg}-${size}`);
      if (!wrapper) return;

      if (val === 'default') {
        // Auto-select default products, lock editing
        wrapper.querySelectorAll('.cs-product-item').forEach((item) => {
          item.classList.remove('csactive');
          item.setAttribute('aria-pressed', 'false');
          item.dataset.disabled = 'true';
        });
        wrapper.querySelectorAll('.default-product').forEach((item) => {
          item.classList.add('csactive');
          item.setAttribute('aria-pressed', 'true');
        });

        // Hide non-defaults
        wrapper.querySelectorAll('.cs-product-item:not(.csactive)').forEach((item) => hide(item));

        // Show message card notes
        if (this.notesWrap) show(this.notesWrap);
      } else {
        // Desired — allow free picking
        wrapper.querySelectorAll('.cs-product-item').forEach((item) => {
          item.classList.remove('csactive', 'cserror');
          item.setAttribute('aria-pressed', 'false');
          delete item.dataset.disabled;
          show(item);
        });

        if (this.notesWrap) hide(this.notesWrap);
      }

      // Hide notes for combos that don't support them
      const selectedBucket = `${pkg}-${size}`;
      const noNotesCombos = ['basket-mini', 'bucket-superbe', 'bucket-petit'];
      if (noNotesCombos.includes(selectedBucket) && this.notesWrap) {
        hide(this.notesWrap);
      }

      this._recalcTotal();
    }

    _onProductClick(item) {
      if (item.dataset.disabled === 'true') return;

      const showProd = item.dataset.showprod;
      const limit = parseInt(item.dataset.limit || '1', 10);
      const isActive = item.classList.contains('csactive');

      item.classList.toggle('csactive', !isActive);
      item.setAttribute('aria-pressed', (!isActive).toString());

      // Enforce limit within this product type
      const typeContainer = this.root.querySelector(`.${showProd.replace('-', '-').split('-').slice(0, 2).join('-')}, [data-combo="${showProd}"]`) ||
        item.closest('.parent-products-type');

      if (typeContainer) {
        const activeItems = typeContainer.querySelectorAll('.cs-product-item.csactive');
        const atLimit = activeItems.length >= limit;

        typeContainer.querySelectorAll('.cs-product-item:not(.csactive)').forEach((other) => {
          other.dataset.disabled = atLimit ? 'true' : '';
        });
      }

      // Show actions if any selection made
      const anyActive = this.root.querySelectorAll('.cs-product-item.csactive').length > 0;
      if (anyActive) show(this.giftActions);

      // Optional product — toggle notes
      if (item.classList.contains('optional-product')) {
        const anyOptionalActive = this.root.querySelectorAll('.optional-product.csactive').length > 0;
        if (this.notesWrap) setVisible(this.notesWrap, anyOptionalActive);
        if (this.notesTextarea) {
          if (anyOptionalActive) {
            this.notesTextarea.setAttribute('required', '');
          } else {
            this.notesTextarea.removeAttribute('required');
          }
        }
      }

      this._recalcTotal();
    }

    // ── UI helpers ─────────────────────────────────────────────────────────

    _clearProductSelections() {
      this.root.querySelectorAll('.cs-product-item').forEach((item) => {
        item.classList.remove('csactive', 'cserror');
        item.setAttribute('aria-pressed', 'false');
        delete item.dataset.disabled;
        show(item);
      });
      this.totalPrice = 0;
      if (this.totalPriceEl) this.totalPriceEl.textContent = '';
    }

    _showProductsForCombo(pkg, size) {
      // Hide all product wrappers
      this.root.querySelectorAll('.products-wrapper').forEach((w) => {
        w.classList.add('ds-none');
        w.querySelectorAll('.parent-products-type').forEach((t) => t.classList.remove('active-proudcts-list'));
      });

      const target = this.root.querySelector(`.ds-${pkg}-${size}`);
      if (target) {
        target.classList.remove('ds-none');
        target.querySelectorAll('.parent-products-type').forEach((t) => {
          t.classList.add('active-proudcts-list');
          // Hide empty types
          if (t.querySelectorAll('.cs-product-item').length === 0) {
            t.classList.add('ds-none');
          } else {
            t.classList.remove('ds-none');
          }
        });
        show(this.productsArea);
      }
    }

    // _updatePackageImage() {
    //   const img = this.root.querySelector('.gift-product__package-image');
    //   if (!img || !this.selectedPackage || !this.selectedSize) return;

    //   // Attempt to build file URL from Shopify CDN pattern
    //   // File URL base is injected by Liquid — fallback to empty on failure
    //   const fileBase = window.GiftProductFileBase || '';
    //   if (fileBase) {
    //     const newSrc = `${fileBase}${this.selectedPackage}-${this.selectedSize}.jpg`;
    //     img.src = newSrc;
    //     show(img);
    //   }
    // }
    _updatePackageImage() {
  const img = this.root.querySelector('.gift-product__package-image');

  if (!img) return;

  if (!this.selectedPackage || !this.selectedSize) {
    hide(img);
    return;
  }

  const packageName = String(this.selectedPackage)
    .trim()
    .toLowerCase();

  const sizeName = String(this.selectedSize)
    .trim()
    .toLowerCase();

  const fileName = `${packageName}-${sizeName}.jpg`;

  /*
   * Shopify Files CDN base URL.
   *
   * IMPORTANT:
   * This should point to your Shopify Files directory
   * and must end with a slash.
   */
  const fileBase =
    window.GiftProductFileBase ||
    'https://cdn.shopify.com/s/files/1/0586/0438/8523/files/';

  const imageUrl = `${fileBase}${fileName}`;

  console.log('[GiftProduct] Package:', packageName);
  console.log('[GiftProduct] Size:', sizeName);
  console.log('[GiftProduct] Image:', imageUrl);

  // Remove previous image
  img.removeAttribute('src');

  // Set new image
  img.src = imageUrl;
  img.alt = `${packageName} ${sizeName}`;
  img.style.display = 'block';
  img.removeAttribute('aria-hidden');

  // Successfully loaded
  img.onload = () => {
    show(img);
  };

  // Image doesn't exist
  img.onerror = () => {
    console.warn(
      '[GiftProduct] Image not found:',
      imageUrl
    );

    img.removeAttribute('src');
    hide(img);
  };
}

    _updateDescription() {
      this.root.querySelectorAll('.gift-product__package-text').forEach((p) => hide(p));
      const comboKey = `${this.selectedPackage}-${this.selectedSize}`;
      const activeDesc = this.root.querySelector(`.desc-${comboKey}`);
      if (activeDesc) show(activeDesc);
    }

    _getBasketPrice() {
      const key = `${this.selectedPackage}-${this.selectedSize}`;
      // Basket price refs are rendered as .ds-basket-ref spans
      const basketRef = document.querySelector(`.ds-basket-ref.${CSS.escape(key)}`);
      return basketRef ? parseFloat(basketRef.dataset.price || '0') : 0;
    }

    _recalcTotal() {
      let total = this._getBasketPrice();
      this.root.querySelectorAll('.cs-product-item.csactive').forEach((item) => {
        total += parseFloat(item.dataset.price || '0');
      });
      this.totalPrice = total;
      if (this.totalPriceEl) {
        this.totalPriceEl.textContent = formatter.format(total);
      }
    }

    // ── Add to cart ────────────────────────────────────────────────────────

    _onAddToCart() {
      const pkg = this.selectedPackage;
      const size = this.selectedSize;

      if (!pkg || !size) {
        this._showError();
        return;
      }

      // Validate delivery date
      if (!this.deliveryInput || !this.deliveryInput.value) {
        if (this.timeslotError) show(this.timeslotError);
        this.deliveryInput && this.deliveryInput.focus();
        return;
      }
      if (this.timeslotError) hide(this.timeslotError);

      // Validate required product types
      const activeWrappers = this.root.querySelectorAll(
        '.products-wrapper.active-proudcts-list:not(.optional-products-wrapper):not(.ds-none), ' +
        `.ds-${pkg}-${size} .parent-products-type.active-proudcts-list:not(.ds-optional):not(.ds-none)`
      );

      // Collect via the combo wrapper instead
      const comboWrapper = this.root.querySelector(`.ds-${pkg}-${size}`);
      if (!comboWrapper) {
        this._showError();
        return;
      }

      const requiredTypes = comboWrapper.querySelectorAll(
        '.parent-products-type:not(.ds-optional):not(.ds-none)'
      );

      let valid = true;
      requiredTypes.forEach((typeEl) => {
        if (typeEl.classList.contains('ds-none')) return;
        const limit = parseInt(typeEl.dataset.limit || '0', 10);
        if (limit === 0) return;
        const selected = typeEl.querySelectorAll('.cs-product-item.csactive').length;
        if (selected !== limit) {
          valid = false;
          typeEl.querySelectorAll('.cs-product-item:not(.csactive)').forEach((item) => flashError(item));
        }
      });

      if (!valid) {
        this._showError();
        return;
      }

      // Validate stock vs quantity
      const qty = parseInt(this.qtyInput ? this.qtyInput.value : '1', 10) || 1;
      let stockOk = true;
      this.root.querySelectorAll('.cs-product-item.csactive').forEach((item) => {
        const avQty = parseInt(item.dataset.avqty || '999', 10);
        if (avQty < qty) stockOk = false;
      });

      if (!stockOk) {
        this._showError();
        return;
      }

      // Build items array
      const items = [];
      const comboKey = `${pkg}-${size}`;
      let bundleUnique = '';

      this.root.querySelectorAll('.cs-product-item.csactive').forEach((item) => {
        const vid = parseInt(item.dataset.variantid, 10);
        const avQty = parseInt(item.dataset.avqty || '999', 10);
        const itemQty = Math.min(qty, avQty);
        bundleUnique += `${vid}:`;
        items.push({
          id: vid,
          quantity: itemQty,
          properties: {
            For: `${pkg.toUpperCase()} ${size.toUpperCase()}`,
          },
        });
      });

      bundleUnique = bundleUnique.slice(0, -1); // remove trailing colon

      // Find basket variant ID
      const basketRef = document.querySelector(`.ds-basket-ref.${CSS.escape(comboKey)}`);
      const basketId = basketRef ? parseInt(basketRef.dataset.id, 10) : null;

      if (!basketId) {
        console.warn('[GiftProduct] No basket variant ID found for', comboKey);
        this._showError();
        return;
      }

      const basket = {
        id: basketId,
        quantity: qty,
        properties: {
          Notes: this.notesTextarea ? this.notesTextarea.value : '',
          'Delivery Date': this.deliveryInput.value,
          'Special Instructions': this.instructionsTextarea ? this.instructionsTextarea.value : '',
          basket_item_keys: `${bundleUnique}:${basketId}`,
        },
      };

      items.push(basket);

      // Disable add button to prevent double-click
      const addBtn = this.root.querySelector('.addtobtn');
      if (addBtn) {
        addBtn.setAttribute('aria-disabled', 'true');
        addBtn.style.opacity = '0.6';
        addBtn.style.pointerEvents = 'none';
      }

      cartAdd({ items })
        .then((response) => {
          if (response.status === 200) {
            // Trigger cart refresh event (FoxTheme cart drawer)
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            // Redirect to cart after short delay
            setTimeout(() => {
              window.location.href = '/cart';
            }, 600);
          } else {
            return response.json().then((data) => {
              console.error('[GiftProduct] Cart add error:', data);
              this._showError();
              this._resetAddBtn(addBtn);
            });
          }
        })
        .catch((err) => {
          console.error('[GiftProduct] Fetch error:', err);
          this._showError();
          this._resetAddBtn(addBtn);
        });
    }

    _resetAddBtn(addBtn) {
      if (!addBtn) return;
      addBtn.removeAttribute('aria-disabled');
      addBtn.style.opacity = '';
      addBtn.style.pointerEvents = '';
    }

    _showError() {
      if (!this.errorOverlay) return;
      show(this.errorOverlay);
      setTimeout(() => hide(this.errorOverlay), 5000);
    }
  }

  // ─── Bootstrap ────────────────────────────────────────────────────────────

  function initGiftProducts() {
    const configs = window.GiftProductSections || {};
    Object.values(configs).forEach((cfg) => {
      try {
        new GiftProduct(cfg);
      } catch (err) {
        console.error('[GiftProduct] Init error for section', cfg.sectionId, err);
      }
    });
  }

  // Respect FoxTheme's DOMready if available, otherwise use DOMContentLoaded
  if (window.FoxTheme && typeof window.FoxTheme.DOMready === 'function') {
    window.FoxTheme.DOMready(initGiftProducts);
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGiftProducts);
  } else {
    initGiftProducts();
  }

  // Re-init on Shopify design-mode section reload
  document.addEventListener('shopify:section:load', (e) => {
    const sectionId = e.detail && e.detail.sectionId;
    if (!sectionId) return;
    const cfg = window.GiftProductSections && window.GiftProductSections[sectionId];
    if (cfg) {
      setTimeout(() => {
        try {
          new GiftProduct(cfg);
        } catch (err) {
          console.error('[GiftProduct] Re-init error:', err);
        }
      }, 200);
    }
  });
})();